/**
 * The AI section lists the account's assistants, each holding the same five
 * sections as its sub-menu. The sections used to be a tab bar in the page
 * header; the sidebar carries them now, so an assistant's pages are reachable
 * without opening the assistant first.
 *
 * Each section stays highlighted on the sub-pages it owns (`activeOn`), and
 * every leaf carries the assistant in its route params - that is what lets the
 * sidebar tell one assistant's Overview from another's.
 */
const buildSections = ({
  t,
  accountScopedRoute,
  assistantId,
  hasCaptainV2,
}) => {
  const sectionRoute = name => accountScopedRoute(name, { assistantId });

  const sections = [
    {
      key: 'overview',
      label: t('CAPTAIN.TABS.OVERVIEW'),
      to: sectionRoute('captain_assistants_overview_index'),
      activeOn: ['captain_assistants_overview_index'],
    },
    {
      key: 'profile',
      label: t('CAPTAIN.TABS.PROFILE'),
      to: sectionRoute('captain_assistants_settings_index'),
      activeOn: ['captain_assistants_settings_index'],
    },
    {
      key: 'skills',
      label: t('CAPTAIN.TABS.SKILLS'),
      to: sectionRoute('captain_assistants_scenarios_index'),
      activeOn: ['captain_assistants_scenarios_index', 'captain_tools_index'],
      captainV2Only: true,
    },
    {
      key: 'knowledge',
      label: t('CAPTAIN.TABS.KNOWLEDGE'),
      to: sectionRoute('captain_assistants_documents_index'),
      activeOn: [
        'captain_assistants_documents_index',
        'captain_assistants_responses_index',
        'captain_assistants_faq_suggestions',
      ],
    },
    {
      key: 'guidelines',
      label: t('CAPTAIN.TABS.GUIDELINES'),
      to: sectionRoute('captain_assistants_guidelines_index'),
      activeOn: ['captain_assistants_guidelines_index'],
      captainV2Only: true,
    },
    {
      key: 'advanced',
      label: t('CAPTAIN.TABS.ADVANCED'),
      to: sectionRoute('captain_assistants_guardrails_index'),
      activeOn: [
        'captain_assistants_guardrails_index',
        'captain_assistants_inboxes_index',
      ],
      captainV2Only: true,
    },
  ];

  return sections
    .filter(section => !section.captainV2Only || hasCaptainV2)
    .map(({ key, label, to, activeOn }) => ({
      name: `captain-assistant-${assistantId}-${key}`,
      label,
      to,
      activeOn,
    }));
};

export default ({
  t,
  accountScopedRoute,
  captainAssistants,
  hasCaptainV2,
}) => ({
  name: 'Captain',
  icon: 'i-woot-captain',
  label: t('SIDEBAR.CAPTAIN'),
  activeOn: ['captain_assistants_create_index', 'captain_assistants_new'],
  children: captainAssistants.map(assistant => ({
    name: `captain-assistant-${assistant.id}`,
    label: assistant.name,
    icon: 'i-ph-sparkle',
    collapsible: true,
    showTreeLine: true,
    children: buildSections({
      t,
      accountScopedRoute,
      assistantId: assistant.id,
      hasCaptainV2,
    }),
  })),
});
